import { useState, useEffect } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Link, useSearchParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { meApi, billingApi, githubApi, apiTokensApi, type ApiToken } from '../api/client'
import { isSelfHosted } from '../config/edition'
import { useAuthStore } from '../store/authStore'
import { api } from '../api/client'
import { useInstances } from '../hooks/useInstances'
import GrantBanner from '../components/GrantBanner'
import { Badge, Button, Card, CardHeader, Checkbox, Field, Input, LinkButton, Skeleton } from '../components/ui'

// ── Avatar helpers ──────────────────────────────────────────────────────────

const AVATAR_COLORS = [
  'bg-blue-500', 'bg-emerald-500', 'bg-violet-500', 'bg-rose-500',
  'bg-amber-500', 'bg-cyan-500', 'bg-indigo-500', 'bg-teal-500',
]

export function getAvatarColor(email: string): string {
  let hash = 0
  for (let i = 0; i < email.length; i++) hash = (hash * 31 + email.charCodeAt(i)) | 0
  return AVATAR_COLORS[Math.abs(hash) % AVATAR_COLORS.length]
}

export function getInitials(email: string): string {
  const local = email.split('@')[0]
  const parts = local.split(/[._-]/).filter(Boolean)
  if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase()
  return local.slice(0, 2).toUpperCase()
}

// Axios error shape the API returns on 4xx: { error: "..." }.
function apiError(e: unknown): string | undefined {
  return (e as { response?: { data?: { error?: string } } } | undefined)?.response?.data?.error
}

// ── Change password form ────────────────────────────────────────────────────

function ChangePasswordForm() {
  const { t } = useTranslation()
  const [current, setCurrent] = useState('')
  const [next, setNext] = useState('')
  const [confirm, setConfirm] = useState('')
  const [success, setSuccess] = useState(false)

  const mutation = useMutation({
    mutationFn: (body: { current_password: string; new_password: string }) =>
      api.post('/auth/password', body),
  })

  const validationError =
    next.length > 0 && next.length < 8
      ? t('changePassword.minLength')
      : next !== confirm && confirm.length > 0
      ? t('changePassword.mismatch')
      : null

  const canSubmit = current.length > 0 && next.length >= 8 && next === confirm && !mutation.isPending

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    if (!canSubmit) return
    setSuccess(false)
    mutation.mutate(
      { current_password: current, new_password: next },
      {
        onSuccess: () => {
          setSuccess(true)
          setCurrent('')
          setNext('')
          setConfirm('')
        },
      },
    )
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <Field label={t('changePassword.current')}>
        {(p) => (
          <Input
            {...p}
            type="password"
            value={current}
            onChange={(e) => setCurrent(e.target.value)}
            autoComplete="current-password"
          />
        )}
      </Field>

      <Field label={t('changePassword.new')}>
        {(p) => (
          <Input
            {...p}
            type="password"
            value={next}
            onChange={(e) => setNext(e.target.value)}
            autoComplete="new-password"
          />
        )}
      </Field>

      <Field label={t('changePassword.confirm')} error={validationError ?? undefined}>
        {(p) => (
          <Input
            {...p}
            type="password"
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
            autoComplete="new-password"
            invalid={Boolean(validationError)}
          />
        )}
      </Field>

      {mutation.isError && (
        <p className="text-sm text-red-600">{apiError(mutation.error) ?? t('changePassword.error')}</p>
      )}
      {success && <p className="text-sm text-green-600">{t('changePassword.success')}</p>}

      <Button type="submit" disabled={!canSubmit} loading={mutation.isPending}>
        {mutation.isPending ? t('changePassword.submitting') : t('changePassword.submit')}
      </Button>
    </form>
  )
}

// ── Plan snapshot (compact overview; full controls live on /billing) ─────────

const PLAN_DISPLAY: Record<string, string> = { free: 'Free', paid: 'Paid' }

function PlanSnapshot() {
  const { t, i18n } = useTranslation()
  const [searchParams] = useSearchParams()
  const checkoutSuccess = searchParams.get('checkout') === 'success'
  const currentPlan = useAuthStore((s) => s.plan) ?? 'free'

  const { data: sub, isLoading } = useQuery({
    queryKey: ['subscription'],
    queryFn: billingApi.getSubscription,
  })
  const { data: me } = useQuery({ queryKey: ['me'], queryFn: meApi.get })
  const { data: instances } = useInstances()

  const plan = sub?.plan ?? currentPlan
  const totalLimit = plan === 'paid' ? sub?.extra_instances ?? 0 : 1
  const used = instances?.length ?? 0
  const usedPct = Math.min(100, Math.round((used / Math.max(totalLimit, 1)) * 100))
  const mailActive = me?.mail_enabled || me?.mail_has_paid_sub

  if (isLoading) {
    return (
      <div className="space-y-3">
        <Skeleton className="h-6 w-1/3" />
        <Skeleton className="h-2 w-full" />
        <Skeleton className="h-4 w-2/3" />
      </div>
    )
  }

  const statusTone =
    sub?.status === 'active' || !sub?.status ? 'success' : sub?.status === 'canceled' ? 'danger' : 'warning'

  return (
    <div className="space-y-4">
      {checkoutSuccess && (
        <div className="bg-green-50 border border-green-200 text-green-800 text-sm rounded-lg px-4 py-3">
          {t('billing.checkoutSuccess')}
        </div>
      )}

      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2.5">
          <span className="text-2xl font-bold text-gray-900">
            {sub?.plan_expires_at ? t('billing.planGranted') : (PLAN_DISPLAY[plan] ?? plan)}
          </span>
          <Badge tone={statusTone}>
            {sub?.status ? t(`billing.status.${sub.status}`) : t('billing.status.active')}
          </Badge>
        </div>
        <Link to="/pricing" className="text-sm text-brand-600 hover:text-brand-700 font-medium shrink-0">
          {plan === 'free' ? t('billing.upgrade') : t('billing.addMoreInstances')}
        </Link>
      </div>

      {/* Time-limited grant — the "when does my free access end" answer */}
      {sub?.plan_expires_at && <GrantBanner expiresAt={sub.plan_expires_at} />}

      {/* Instance usage */}
      <div>
        <div className="flex items-baseline justify-between mb-1.5">
          <span className="text-xs font-medium text-gray-500 uppercase tracking-wide">{t('billing.usageTitle')}</span>
          <span className="text-sm text-gray-700 tabular-nums">
            <span className="font-semibold text-gray-900">{used}</span>
            <span className="text-gray-400"> / {totalLimit}</span>
          </span>
        </div>
        <div className="h-2 rounded-full bg-gray-100 overflow-hidden">
          <div
            className={`h-full rounded-full transition-all ${usedPct >= 100 ? 'bg-amber-500' : 'bg-brand-500'}`}
            style={{ width: `${Math.max(usedPct, used > 0 ? 6 : 0)}%` }}
          />
        </div>
      </div>

      {/* Renewal (paid, no grant) */}
      {!sub?.plan_expires_at && sub?.next_charge_at && sub.status !== 'canceled' && (
        <p className="text-sm text-gray-500">
          {t('billing.nextRenewal', { date: new Date(sub.next_charge_at).toLocaleDateString(i18n.language) })}
        </p>
      )}
      {sub?.canceled_at && (
        <p className="text-sm text-amber-600">
          {t('billing.canceledOn', { date: new Date(sub.canceled_at).toLocaleDateString(i18n.language) })}
        </p>
      )}

      {/* Mail add-on */}
      <div className="flex items-center gap-2 text-sm text-gray-500">
        <span className={`w-1.5 h-1.5 rounded-full ${mailActive ? 'bg-green-500' : 'bg-gray-300'}`} />
        {mailActive ? t('billing.mailAddonShortOn') : t('billing.mailAddonShortOff')}
      </div>

      <div className="pt-3 border-t border-gray-100">
        <Link to="/billing" className="inline-flex items-center gap-1 text-sm text-brand-600 hover:text-brand-700 font-medium">
          {t('billing.manageBilling')}
          <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
          </svg>
        </Link>
      </div>
    </div>
  )
}

// ── Notification preferences ─────────────────────────────────────────────────

function NotificationPreferences() {
  const { t } = useTranslation()
  const queryClient = useQueryClient()
  const { data: user, isLoading } = useQuery({
    queryKey: ['me'],
    queryFn: meApi.get,
    staleTime: 5 * 60 * 1000,
  })

  const [saveError, setSaveError] = useState('')
  const mutation = useMutation({
    mutationFn: meApi.update,
    onSuccess: () => {
      setSaveError('')
      queryClient.invalidateQueries({ queryKey: ['me'] })
    },
    // A failed toggle used to snap back on the next `me` refetch with no
    // explanation, which reads as the switch being broken.
    onError: () => setSaveError(t('notifications.saveFailed')),
  })

  if (isLoading || !user) {
    return (
      <div className="space-y-3">
        {[...Array(4)].map((_, i) => (
          <div key={i} className="flex items-center justify-between">
            <div className="space-y-1">
              <Skeleton className="h-4 w-40" />
              <Skeleton className="h-3 w-64" />
            </div>
            <Skeleton className="h-6 w-11 rounded-full" />
          </div>
        ))}
      </div>
    )
  }

  const prefs = [
    {
      key: 'notify_instance_ready' as const,
      label: t('notifications.instanceReady'),
      description: t('notifications.instanceReadyDesc'),
      value: user.notify_instance_ready,
    },
    {
      key: 'notify_provision_failed' as const,
      label: t('notifications.provisionFailed'),
      description: t('notifications.provisionFailedDesc'),
      value: user.notify_provision_failed,
    },
    {
      key: 'notify_backup_failed' as const,
      label: t('notifications.backupFailed'),
      description: t('notifications.backupFailedDesc'),
      value: user.notify_backup_failed,
    },
    {
      key: 'marketing_emails' as const,
      label: t('notifications.marketingEmails'),
      description: t('notifications.marketingEmailsDesc'),
      value: user.marketing_emails,
    },
    // Legitimate-interest in-app usage measurement; the field is positive
    // ("enabled") so this generic toggle machinery maps to it directly.
    {
      key: 'usage_tracking_enabled' as const,
      label: t('notifications.usageTracking'),
      description: t('notifications.usageTrackingDesc'),
      value: user.usage_tracking_enabled ?? true,
    },
  ]

  return (
    <div className="space-y-4">
      {saveError && (
        <p className="text-sm text-red-600" role="alert">{saveError}</p>
      )}
      {prefs.map((pref) => (
        <div key={pref.key} className="flex items-start justify-between gap-4">
          <div className="min-w-0">
            <p className="text-sm font-medium text-gray-800">{pref.label}</p>
            <p className="text-xs text-gray-500 mt-0.5">{pref.description}</p>
          </div>
          {/* A sliding switch, not a Button: role=switch + aria-checked is the
              interaction, and Button's padding/variants would break the track. */}
          <button
            type="button"
            role="switch"
            aria-checked={pref.value}
            aria-label={pref.label}
            disabled={mutation.isPending}
            onClick={() => mutation.mutate({ [pref.key]: !pref.value })}
            className={`relative inline-flex h-6 w-11 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-brand-500 focus:ring-offset-2 disabled:opacity-50 ${
              pref.value ? 'bg-brand-600' : 'bg-gray-200'
            }`}
          >
            <span
              className={`pointer-events-none inline-block h-5 w-5 rounded-full bg-white shadow ring-0 transition-transform duration-200 ${
                pref.value ? 'translate-x-5' : 'translate-x-0'
              }`}
            />
          </button>
        </div>
      ))}
    </div>
  )
}

// ── GitHub integration ──────────────────────────────────────────────────────

const GITHUB_MARK = (
  <path d="M12 0C5.37 0 0 5.37 0 12c0 5.31 3.435 9.795 8.205 11.385.6.105.825-.255.825-.57 0-.285-.015-1.23-.015-2.235-3.015.555-3.795-.735-4.035-1.41-.135-.345-.72-1.41-1.23-1.695-.42-.225-1.02-.78-.015-.795.945-.015 1.62.87 1.845 1.23 1.08 1.815 2.805 1.305 3.495.99.105-.78.42-1.305.765-1.605-2.67-.3-5.46-1.335-5.46-5.925 0-1.305.465-2.385 1.23-3.225-.12-.3-.54-1.53.12-3.18 0 0 1.005-.315 3.3 1.23.96-.27 1.98-.405 3-.405s2.04.135 3 .405c2.295-1.56 3.3-1.23 3.3-1.23.66 1.65.24 2.88.12 3.18.765.84 1.23 1.905 1.23 3.225 0 4.605-2.805 5.625-5.475 5.925.435.375.81 1.095.81 2.22 0 1.605-.015 2.895-.015 3.3 0 .315.225.69.825.57A12.02 12.02 0 0024 12c0-6.63-5.37-12-12-12z" />
)

function GitHubConnection() {
  const { t } = useTranslation()
  const queryClient = useQueryClient()
  const [searchParams, setSearchParams] = useSearchParams()
  const githubStatus = searchParams.get('github')
  const [toast, setToast] = useState<'connected' | 'error' | 'in_use' | 'disconnectError' | null>(null)

  const { data: user } = useQuery({
    queryKey: ['me'],
    queryFn: meApi.get,
    staleTime: 5 * 60 * 1000,
  })

  useEffect(() => {
    if (githubStatus === 'connected' || githubStatus === 'error' || githubStatus === 'in_use') {
      setToast(githubStatus)
      queryClient.invalidateQueries({ queryKey: ['me'] })
      const newParams = new URLSearchParams(searchParams)
      newParams.delete('github')
      setSearchParams(newParams, { replace: true })
      const timer = setTimeout(() => setToast(null), 6000)
      return () => clearTimeout(timer)
    }
  }, [githubStatus, queryClient, searchParams, setSearchParams])

  const disconnectMutation = useMutation({
    mutationFn: githubApi.disconnect,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['me'] })
    },
    // Without this a failed disconnect did nothing visible: the account stayed
    // linked and the user had no idea why.
    onError: () => setToast('disconnectError'),
  })

  // Was a bare `api.get(...).then(...)`: a rejected redirect lookup left the
  // user staring at a button that did nothing (and floated the rejection).
  const connectMutation = useMutation({
    mutationFn: () => api.get<{ url: string }>('/github/connect').then((res) => res.data.url),
    onSuccess: (url) => {
      window.location.href = url
    },
    onError: () => setToast('error'),
  })

  const connected = !!user?.github_username
  // When the user has no local password, GitHub is their only way in. Block
  // disconnect and nudge them to set a password first.
  const canDisconnect = !!user?.has_password

  return (
    <div className="space-y-4">
      {toast === 'connected' && (
        <div className="bg-green-50 border border-green-200 text-green-800 text-sm rounded-lg px-4 py-3">
          {t('github.connectSuccess')}
        </div>
      )}
      {toast === 'error' && (
        <div className="bg-red-50 border border-red-200 text-red-700 text-sm rounded-lg px-4 py-3" role="alert">
          {t('github.connectError')}
        </div>
      )}
      {toast === 'disconnectError' && (
        <div className="bg-red-50 border border-red-200 text-red-700 text-sm rounded-lg px-4 py-3" role="alert">
          {t('github.disconnectError')}
        </div>
      )}
      {toast === 'in_use' && (
        <div className="bg-amber-50 border border-amber-200 text-amber-800 text-sm rounded-lg px-4 py-3">
          {t('github.alreadyLinked')}
        </div>
      )}

      {connected && !canDisconnect && (
        <div className="bg-amber-50 border border-amber-200 text-amber-800 text-sm rounded-lg px-4 py-3">
          {t('github.setPasswordFirst')}{' '}
          <a href="/forgot-password" className="font-medium underline hover:text-amber-900">
            {t('github.setPasswordLink')}
          </a>
        </div>
      )}

      {connected ? (
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-full bg-ink-900 flex items-center justify-center">
              <svg className="w-5 h-5 text-white" viewBox="0 0 24 24" fill="currentColor">
                {GITHUB_MARK}
              </svg>
            </div>
            <div>
              <p className="text-sm font-medium text-gray-900">
                {t('github.connected', { username: user.github_username })}
              </p>
              {user.github_connected_at && (
                <p className="text-xs text-gray-500">
                  {t('github.connectedSince', {
                    date: new Date(user.github_connected_at).toLocaleDateString(),
                  })}
                </p>
              )}
            </div>
          </div>
          <LinkButton
            tone="danger"
            onClick={() => disconnectMutation.mutate()}
            disabled={disconnectMutation.isPending || !canDisconnect}
            title={!canDisconnect ? t('github.setPasswordFirst') : undefined}
            className="shrink-0"
          >
            {t('github.disconnect')}
          </LinkButton>
        </div>
      ) : (
        <div className="flex items-center justify-between gap-3">
          <p className="text-sm text-gray-500">{t('github.description')}</p>
          {/* Deliberately dark (`ink`) brand button — no Button variant paints
              a vendor mark surface, so this one stays hand-rolled. */}
          <button
            type="button"
            onClick={() => connectMutation.mutate()}
            disabled={connectMutation.isPending}
            className="inline-flex items-center gap-2 bg-ink-900 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-ink-800 transition-colors shrink-0 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor">
              {GITHUB_MARK}
            </svg>
            {t('github.connect')}
          </button>
        </div>
      )}
    </div>
  )
}

// ── API tokens (multi-token auth for MCP server, CI bots) ───────────────────

function ApiTokensSection() {
  const { t } = useTranslation()
  const queryClient = useQueryClient()
  const [creating, setCreating] = useState(false)
  const [name, setName] = useState('')
  const [selectedScopes, setSelectedScopes] = useState<string[]>([])
  const [expiresInDays, setExpiresInDays] = useState<string>('')
  const [revealed, setRevealed] = useState<ApiToken | null>(null)
  const [copied, setCopied] = useState(false)
  const [confirmRevoke, setConfirmRevoke] = useState<string | null>(null)

  const { data: tokens, isLoading } = useQuery({
    queryKey: ['api-tokens'],
    queryFn: apiTokensApi.list,
    staleTime: 60_000,
  })

  const { data: availableScopes } = useQuery({
    queryKey: ['api-tokens', 'scopes'],
    queryFn: apiTokensApi.scopes,
    staleTime: 5 * 60 * 1000,
  })

  const create = useMutation({
    mutationFn: apiTokensApi.create,
    onSuccess: (tok) => {
      setRevealed(tok)
      setName('')
      setSelectedScopes([])
      setExpiresInDays('')
      setCreating(false)
      queryClient.invalidateQueries({ queryKey: ['api-tokens'] })
    },
  })

  const revoke = useMutation({
    mutationFn: apiTokensApi.revoke,
    onSuccess: () => {
      setConfirmRevoke(null)
      queryClient.invalidateQueries({ queryKey: ['api-tokens'] })
    },
  })

  const toggleScope = (s: string) => {
    setSelectedScopes((cur) => (cur.includes(s) ? cur.filter((x) => x !== s) : [...cur, s]))
  }

  const submit = (e: React.FormEvent) => {
    e.preventDefault()
    if (!name.trim()) return
    const days = expiresInDays ? parseInt(expiresInDays, 10) : undefined
    create.mutate({
      name: name.trim(),
      scopes: selectedScopes,
      expires_in_days: days && days > 0 ? days : undefined,
    })
  }

  const copyToken = async () => {
    if (!revealed?.token) return
    try {
      await navigator.clipboard.writeText(revealed.token)
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    } catch {
      // Browsers without clipboard permission — user can select manually.
    }
  }

  return (
    <div className="space-y-4">
      <p className="text-sm text-gray-500">{t('apiTokens.description')}</p>

      {revealed?.token && (
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 space-y-3">
          <p className="text-sm font-medium text-amber-900">{t('apiTokens.copyOnce')}</p>
          <div className="flex items-center gap-2">
            <code className="flex-1 font-mono text-xs bg-white border border-amber-200 rounded px-3 py-2 break-all">
              {revealed.token}
            </code>
            {/* Amber action inside an amber alert — Button has no warning
                variant, and a brand-blue button here would read as unrelated. */}
            <button
              type="button"
              onClick={copyToken}
              className="text-sm px-3 py-2 bg-amber-600 text-white rounded-lg hover:bg-amber-700 shrink-0"
            >
              {copied ? t('apiTokens.copied') : t('apiTokens.copy')}
            </button>
          </div>
          <button
            type="button"
            onClick={() => setRevealed(null)}
            className="text-xs text-amber-900 hover:underline"
          >
            {t('apiTokens.dismiss')}
          </button>
        </div>
      )}

      {isLoading ? (
        <Skeleton className="h-10 w-full" />
      ) : tokens && tokens.length > 0 ? (
        <ul className="divide-y divide-gray-100 border border-gray-200 rounded-lg overflow-hidden">
          {tokens.map((tok) => (
            <li key={tok.id} className="px-4 py-3 flex items-start justify-between gap-4">
              <div className="min-w-0">
                <p className="text-sm font-medium text-gray-800 truncate">{tok.name}</p>
                <p className="text-xs text-gray-500 mt-0.5">
                  <code className="font-mono bg-gray-100 rounded px-1.5 py-0.5">
                    {tok.prefix}…
                  </code>
                  {tok.scopes.length > 0 ? (
                    <span className="ml-2">{tok.scopes.join(', ')}</span>
                  ) : (
                    <span className="ml-2 text-gray-400">{t('apiTokens.fullAccess')}</span>
                  )}
                </p>
                <p className="text-xs text-gray-400 mt-0.5">
                  {t('apiTokens.createdAt', { date: new Date(tok.created_at).toLocaleDateString() })}
                  {tok.expires_at && (
                    <span className="ml-2">
                      · {t('apiTokens.expiresAt', { date: new Date(tok.expires_at).toLocaleDateString() })}
                    </span>
                  )}
                  {tok.last_used_at && (
                    <span className="ml-2">
                      · {t('apiTokens.lastUsed', { date: new Date(tok.last_used_at).toLocaleString() })}
                    </span>
                  )}
                </p>
              </div>
              {confirmRevoke === tok.id ? (
                <div className="flex items-center gap-2 shrink-0">
                  <Button
                    size="sm"
                    variant="danger"
                    loading={revoke.isPending}
                    onClick={() => revoke.mutate(tok.id)}
                  >
                    {t('apiTokens.confirmRevoke')}
                  </Button>
                  <LinkButton tone="neutral" onClick={() => setConfirmRevoke(null)}>
                    {t('common.cancel')}
                  </LinkButton>
                </div>
              ) : (
                <LinkButton tone="danger" className="shrink-0" onClick={() => setConfirmRevoke(tok.id)}>
                  {t('apiTokens.revoke')}
                </LinkButton>
              )}
            </li>
          ))}
        </ul>
      ) : (
        <p className="text-sm text-gray-400">{t('apiTokens.empty')}</p>
      )}

      {creating ? (
        <form onSubmit={submit} className="bg-gray-50 border border-gray-200 rounded-lg p-4 space-y-3">
          <Field label={t('apiTokens.nameLabel')}>
            {(p) => (
              <Input
                {...p}
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder={t('apiTokens.namePlaceholder')}
                maxLength={100}
                autoFocus
              />
            )}
          </Field>

          {/* A checkbox group has no single control to point htmlFor at, so the
              group name is a <legend> - which is what names it for a11y. */}
          <fieldset className="space-y-1.5">
            <legend className="block text-sm font-medium text-gray-700">{t('apiTokens.scopesLabel')}</legend>
            <p className="text-xs text-gray-500 pb-1">{t('apiTokens.scopesHint')}</p>
            {(availableScopes ?? []).map((s) => (
              <Checkbox
                key={s}
                checked={selectedScopes.includes(s)}
                onChange={() => toggleScope(s)}
                label={
                  <>
                    <code className="font-mono text-xs">{s}</code>
                    <span className="text-xs text-gray-500">{t(`apiTokens.scope.${s}`, '')}</span>
                  </>
                }
              />
            ))}
          </fieldset>

          <Field label={t('apiTokens.expiresLabel')}>
            {(p) => (
              <Input
                {...p}
                type="number"
                min={1}
                max={3650}
                value={expiresInDays}
                onChange={(e) => setExpiresInDays(e.target.value)}
                placeholder={t('apiTokens.expiresPlaceholder')}
              />
            )}
          </Field>

          {create.isError && (
            <p className="text-xs text-red-600">{apiError(create.error) ?? t('apiTokens.createFailed')}</p>
          )}

          <div className="flex items-center gap-3">
            <Button type="submit" size="sm" disabled={!name.trim()} loading={create.isPending}>
              {create.isPending ? t('apiTokens.creating') : t('apiTokens.create')}
            </Button>
            <LinkButton tone="neutral" onClick={() => setCreating(false)}>
              {t('common.cancel')}
            </LinkButton>
          </div>
        </form>
      ) : (
        <Button
          onClick={() => {
            setSelectedScopes(availableScopes ?? [])
            setCreating(true)
          }}
        >
          {t('apiTokens.newToken')}
        </Button>
      )}
    </div>
  )
}

// ── Profile page ────────────────────────────────────────────────────────────

export default function ProfilePage() {
  const { t, i18n } = useTranslation()
  const role = useAuthStore((s) => s.role)

  const { data: user, isLoading } = useQuery({
    queryKey: ['me'],
    queryFn: meApi.get,
    staleTime: 5 * 60 * 1000,
  })

  const email = user?.email ?? ''
  const avatarColor = email ? getAvatarColor(email) : 'bg-gray-400'
  const initials = email ? getInitials(email) : '?'

  return (
    <div className="p-4 sm:p-8 mx-auto w-full max-w-5xl space-y-6">
      {/* Identity hero — an avatar + email block, not a PageHeader title row. */}
      <Card className="relative overflow-hidden">
        <div className="pointer-events-none absolute -top-20 -right-16 w-56 h-56 rounded-full bg-brand-500/[0.07] blur-3xl" />
        <div className="relative flex items-center gap-5">
          {isLoading ? (
            <>
              <Skeleton className="w-20 h-20 rounded-full shrink-0" />
              <div className="space-y-2 flex-1">
                <Skeleton className="h-5 w-48" />
                <Skeleton className="h-4 w-32" />
              </div>
            </>
          ) : (
            <>
              <div
                className={`w-20 h-20 rounded-full ${avatarColor} flex items-center justify-center text-white text-2xl font-bold select-none shrink-0 ring-4 ring-white shadow-sm`}
              >
                {initials}
              </div>
              <div className="min-w-0">
                <h1 className="text-xl font-bold text-gray-900 break-all">{email}</h1>
                <div className="flex flex-wrap items-center gap-2 mt-1.5">
                  <Badge tone={role === 'admin' ? 'accent' : 'neutral'}>
                    {role === 'admin' ? t('common.admin') : t('common.user')}
                  </Badge>
                  {user?.status === 'active' && <Badge tone="success">{t('common.active')}</Badge>}
                  {user?.created_at && (
                    <span className="text-xs text-gray-400">
                      {t('profile.memberSince', {
                        date: new Date(user.created_at).toLocaleDateString(i18n.language, {
                          year: 'numeric',
                          month: 'long',
                        }),
                      })}
                    </span>
                  )}
                </div>
              </div>
            </>
          )}
        </div>
      </Card>

      {/* Two-column body — fills the desktop width the single column wasted */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 items-start">
        {/* Sidebar: plan snapshot + account linking */}
        <div className="lg:col-span-5 space-y-6">
          {role !== 'admin' && !isSelfHosted && (
            <Card>
              <CardHeader title={t('billing.currentPlan')} className="mb-4" />
              <PlanSnapshot />
            </Card>
          )}

          {!isSelfHosted && (
            <Card>
              <CardHeader title={t('github.title')} className="mb-4" />
              <GitHubConnection />
            </Card>
          )}
        </div>

        {/* Main: security + integrations + preferences */}
        <div className="lg:col-span-7 space-y-6">
          <Card>
            <CardHeader title={t('changePassword.title')} className="mb-4" />
            <ChangePasswordForm />
          </Card>

          <Card>
            <CardHeader title={t('apiTokens.title')} description={t('apiTokens.subtitle')} className="mb-4" />
            <ApiTokensSection />
          </Card>

          <Card>
            <CardHeader title={t('notifications.title')} description={t('notifications.subtitle')} className="mb-4" />
            <NotificationPreferences />
          </Card>
        </div>
      </div>
    </div>
  )
}
